import { Formik, Form } from 'formik';
import { useState, useContext, useCallback, useEffect, useRef } from 'react';
import { CSSTransition } from 'react-transition-group';
import { useAnalytics } from 'use-analytics';

import FormField from 'components/common/FormField';
import Error from 'components/common/forms/Error';
import { introFormSchema } from 'components/shared/FormSchemas';
import { UserContext, VoucherContext } from 'components/signUpFunnel/Contexts';
import SignInButtonSection from 'components/signUpFunnel/intro/SignInButtonSection';
import { TEMPO } from 'context/BrandContext';
import localStorage from 'helpers/localStorage';
import useAnalyticsCallback from 'hooks/useAnalyticsCallback';
import useAnalyticsEffect from 'hooks/useAnalyticsEffect';
import {
  SUBMITTING_TEXT,
  LEAD_EVENT,
  SHOW_PROMO_BANNER_LOCAL_STORAGE,
  STANDARD_MEAL_SERVING_SIZE,
} from 'lib/constants';
import * as ErrorLogging from 'services/ErrorLogging';
import RequestService from 'services/RequestService';

import ContinueButton from './ContinueButton';

const IntroContainer = ({ goNext }) => {
  const brand = TEMPO;
  const [zipCodeMessage, setZipCodeMessage] = useState({
    message: '',
    response: '',
    initial: true,
  });
  const [error, setError] = useState('');
  const { user, setUser } = useContext(UserContext);
  const { voucher, setVoucher } = useContext(VoucherContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [disableSubmit, setDisableSubmit] = useState(false);
  const analytics = useAnalytics();
  const errorRef = useRef();

  const voucherCode = document?.querySelector('[data-voucher]')?.getAttribute('data-voucher');
  const referralCode = document?.querySelector('[data-referral]')?.getAttribute('data-referral');
  const submitParams = useCallback(
    values => ({
      mealPlanSlug: TEMPO,
      email: values.introEmail,
      signupZip: values.introZipCode,
      signupVoucher: voucherCode,
      mealKitTraits: [],
      referralCode,
      weeklyMeals: 3,
      servings: STANDARD_MEAL_SERVING_SIZE,
    }),
    [voucherCode, referralCode]
  );

  useAnalyticsEffect(({ events, logEvent }) => {
    logEvent(events.pickMeals[TEMPO].intro.viewed);
  }, []);

  const onSubmit = useAnalyticsCallback(
    async (values, { setSubmitting }) => {
      const submitFn = user?.id ? RequestService.updateWelcome : RequestService.createWelcome;
      setIsSubmitting(true);

      try {
        const response = await submitFn({ params: submitParams(values) });
        setIsSubmitting(false);
        setError('');
        if (!response.ok) {
          const errorMessage = response.error;
          setError(errorMessage);
          setSubmitting(false);
          return response;
        }

        onSuccess(response);
        goNext();
        setSubmitting(false);
        return response;
      } catch (error) {
        setIsSubmitting(false);
        const errorMessage = error.message;
        setError(errorMessage);
        ErrorLogging.captureException(errorMessage);

        return { ok: false, error: errorMessage };
      }
    },
    async ({ logEvent, events, callbackArgs, callbackFnResult }) => {
      if (brand) {
        const response = callbackFnResult;
        const values = callbackArgs[0];

        const analyticsParams = {
          ...submitParams(values),
          ...(response && !response.ok && { error: response.error }),
        };

        logEvent(events.pickMeals[brand].intro.submitted, analyticsParams);
      }
    },
    [user, brand, submitParams, onSuccess]
  );

  const handleZipCodeChange = zipCode => {
    if (zipCode.length === 5) {
      setDisableSubmit(true);
      RequestService.availableZipCode({
        params: {
          zip: zipCode,
          mealPlanSlug: 'tempo',
        },
      }).then(response => {
        setDisableSubmit(false);
        if (response.ok) {
          setZipCodeMessage({
            message: response.zipCodes.message,
            response: response.ok,
            initial: false,
          });
        } else {
          setZipCodeMessage({
            message: response.error,
            response: response.ok,
            initial: false,
          });
        }
      });
    }
  };

  const onSuccess = useCallback(response => {
    try {
      const user = response.user;
      setUser({ ...user });
      setVoucher(user.signupVoucher);
      RequestService.v3.createEvents({ params: { eventName: LEAD_EVENT } });
      analytics.track(LEAD_EVENT, {
        email: user.email,
        externalId: user.uuid,
        phone: user.phone,
        mealPlanSlug: 'tempo',
      });
    } catch (e) {
      ErrorLogging.captureException(e.message);
    }
  }, []);

  const onSignInClick = useAnalyticsCallback(
    null,
    ({ events, logEvent }) => {
      if (brand) {
        logEvent(events.signUp[brand].intro.signInClicked);
      }
    },
    [brand]
  );
  useEffect(() => {
    if (voucher?.showPromoBanner) {
      localStorage.write(SHOW_PROMO_BANNER_LOCAL_STORAGE, true);
    }
  }, [voucher]);
  return (
    <div className="mx-auto max-w-lg text-center">
      <Formik
        initialValues={{
          introZipCode: user?.signupZip || '',
          introEmail: user?.email || '',
          introSmsOptIn: user?.receivesSmsPromotional || false,
          introSmsPhoneNumber: user?.phone || '',
        }}
        enableReinitialize
        validationSchema={introFormSchema}
        validateOnBlur={true}
        onSubmit={onSubmit}
      >
        {formik => (
          <div className="relative mt-2 text-left">
            <Form>
              <div className="relative">
                <FormField
                  label="Zip Code"
                  labelClass="text-tempo-black-100"
                  maxLength={5}
                  inputMode="numeric"
                  autoComplete="false"
                  name="introZipCode"
                  inputClass="input--text"
                  labelBrandStylesOverrides="textMd"
                  onBlur={e => {
                    let zipCode = e.currentTarget.value;
                    handleZipCodeChange(zipCode);
                    formik.handleBlur(e);
                  }}
                  onChange={e => {
                    setZipCodeMessage({
                      message: '',
                      response: '',
                      initial: false,
                    });
                    formik.handleChange(e);
                  }}
                  successMessage={zipCodeMessage.response === true ? zipCodeMessage.message : null}
                  error={
                    formik.touched.introZipCode &&
                    ((zipCodeMessage.response === false && zipCodeMessage.message) ||
                      formik.errors.introZipCode)
                  }
                  required={true}
                />
              </div>
              <div className="relative">
                <FormField
                  type="email"
                  label="Email"
                  labelClass="text-tempo-black-100"
                  autoComplete="email"
                  name="introEmail"
                  disabled={Boolean(user?.id)}
                  inputClass="input--text"
                  labelBrandStylesOverrides="textMd"
                  onBlur={formik.handleBlur}
                  error={formik.touched.introEmail && formik.errors.introEmail}
                  required={true}
                />
              </div>
              <SignInButtonSection
                onClick={onSignInClick}
                isUcl={Boolean(user?.id)}
                brand={brand}
                containerClassName="text-left text-charcoal-90"
                textClassName="text-sm text-charcoal-90"
              />
              <CSSTransition
                in={Boolean(error)}
                classNames="fadeInOut"
                timeout={300}
                unmountOnExit
                nodeRef={errorRef}
              >
                <div ref={errorRef}>
                  <Error message={error} show />
                </div>
              </CSSTransition>
              <ContinueButton
                onContinue={formik.onSubmit}
                disabled={zipCodeMessage.response === false}
                submittingText={disableSubmit ? 'Continue' : SUBMITTING_TEXT}
                isSubmitting={isSubmitting}
                disableSubmit={disableSubmit}
              />
            </Form>
          </div>
        )}
      </Formik>
    </div>
  );
};

IntroContainer.propTypes = {
  goNext: PropTypes.func,
};

export default IntroContainer;
